export const TRUSTED_BROWSER_DAYS = 7;

const DEVICE_ID_KEY = "finlotax_device_id";
const TOKEN_KEY = "finlotax_trusted_device_token";
const EXPIRES_AT_KEY = "finlotax_trusted_device_expires_at";

/** Legacy keys — migrate device id once so browsers keep the same id. */
const LEGACY_DEVICE_ID_KEY = "finlo_trusted_device_id";
const LEGACY_MAP_KEY = "finlo_trusted_devices";

function parseExpiryMs(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const asNumber = Number(value);
  if (Number.isFinite(asNumber) && String(value).trim() !== "") {
    // Treat 10-digit values as seconds.
    if (asNumber < 1e12) return asNumber * 1000;
    return asNumber;
  }
  const asDate = Date.parse(String(value));
  return Number.isNaN(asDate) ? null : asDate;
}

/**
 * Generate device_id ONCE per browser and reuse forever.
 * Never create a new UUID on each login.
 */
export function getOrCreateDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (id) return id;

    // One-time migrate from previous key name.
    const legacy = localStorage.getItem(LEGACY_DEVICE_ID_KEY);
    if (legacy) {
      localStorage.setItem(DEVICE_ID_KEY, legacy);
      return legacy;
    }

    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `dev_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    return `dev_${Date.now()}`;
  }
}

/** Clear token + expiry only; keep device_id. */
export function clearTrustedDeviceToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRES_AT_KEY);
    localStorage.removeItem(LEGACY_MAP_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Returns stored trusted credentials if present and not expired.
 * Clears token (keeps device_id) when expired.
 */
export function getTrustedBrowserCredentials() {
  const deviceId = getOrCreateDeviceId();
  let token = null;
  let expiresAt = null;

  try {
    token = localStorage.getItem(TOKEN_KEY);
    expiresAt = parseExpiryMs(localStorage.getItem(EXPIRES_AT_KEY));
  } catch {
    return { deviceId, token: null, expiresAt: null };
  }

  if (!token) {
    return { deviceId, token: null, expiresAt: null };
  }

  if (!expiresAt || Date.now() >= expiresAt) {
    clearTrustedDeviceToken();
    return { deviceId, token: null, expiresAt: null };
  }

  return { deviceId, token: String(token), expiresAt };
}

/**
 * Persist remember-browser credentials from verify-sms-otp response.
 * Overwrites token + expiry; reuses (or stores) the same device_id.
 */
export function saveTrustedBrowserCredentials({
  token,
  expiresAt,
  deviceId,
} = {}) {
  if (!token) return;

  const id = deviceId || getOrCreateDeviceId();
  try {
    localStorage.setItem(DEVICE_ID_KEY, id);
    localStorage.setItem(TOKEN_KEY, String(token));

    const expiryMs =
      parseExpiryMs(expiresAt) ||
      Date.now() + TRUSTED_BROWSER_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(EXPIRES_AT_KEY, String(expiryMs));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Pick the phone used for login SMS OTP:
 * - employees/admins/managers → personal_phone only (never work phone)
 * - clients → mfa_phone (dedicated MFA number; legacy fallback to phone)
 */
export function resolveLoginOtpPhone(loginUser = {}) {
  const role = String(
    loginUser.user_type || loginUser.role || loginUser.user_role || "",
  )
    .trim()
    .toLowerCase();
  const staffRoles = new Set(["employee", "manager", "admin"]);
  const isStaff =
    staffRoles.has(role) ||
    (!!loginUser.employee_id && role !== "client" && !loginUser.client_id);

  if (isStaff) {
    return (
      loginUser.personal_phone ||
      loginUser.personalPhone ||
      loginUser.personal_mobile ||
      null
    );
  }

  return (
    loginUser.mfa_phone ||
    loginUser.mfaPhone ||
    loginUser.otp_phone ||
    loginUser.otpPhone ||
    null
  );
}

/** True when login should use email OTP (no SMS phone on file, or explicit email). */
export function shouldUseEmailLoginOtp(loginUser = {}, response = {}) {
  const channel = String(
    loginUser.otp_channel || response.otp_channel || "",
  )
    .trim()
    .toLowerCase();
  if (channel === "email") return true;
  if (channel === "sms") return false;
  return !resolveLoginOtpPhone(loginUser);
}
